import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Badge from 'react-bootstrap/Badge';
import Loader from '../../component/loader';
import { FiChevronLeft as ChevronLeft, FiChevronRight as ChevronRight } from "react-icons/fi";
import { TbTruckReturn } from "react-icons/tb";
import { AiOutlineClockCircle, AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import { BsBoxSeam } from "react-icons/bs";
import axios from 'axios';
import Cookies from "js-cookie";
import { decrpt } from "../../component/encryption";
import moment from 'moment';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function ReturnOrderList() {
  const [loader, setLoader] = useState(false);
  const [returnList, setReturnList] = useState([]);
  const [counts, setCounts] = useState({
    all: 0,
    requested: 0,
    approved: 0,
    picked_up: 0,
    refunded: 0,
    denied: 0
  });
  const [selectedStatus, setSelectedStatus] = useState(undefined);

  // Pagination & Search states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();

  const fetchReturnList = async (status) => {
    setLoader(true);
    try {
      const token = decrpt(Cookies.get('s_l'));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const params = {};
      if (status !== undefined) {
        params.status = status;
      }

      const response = await axios.post(`/admin/return/orders/list`, params);
      if (response.data && response.data.data) {
        setReturnList(Array.isArray(response.data.data) ? response.data.data : (response.data.data.list || []));
      }
      if (response.data && response.data.counts) {
        setCounts(response.data.counts);
      }
      setLoader(false);
    } catch (err) {
      console.log('Return list error', err);
      setLoader(false);
    }
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    fetchReturnList(status);
  };

  // Filtered return orders by search
  const filteredList = useMemo(() => {
    if (!Array.isArray(returnList)) return [];
    if (!searchText.trim()) return returnList;
    const q = searchText.trim().toLowerCase();
    return returnList.filter((item) => {
      const orderUuid = (item.order?.order_uuid || "").toLowerCase();
      const userName = (item.user?.name || "").toLowerCase();
      const userMobile = (item.user?.mobile || "").toLowerCase();
      const prodName = (item.product?.name || "").toLowerCase();
      const reason = (item.return_reason || item.reason?.reason || "").toLowerCase();
      const id = String(item.id || "").toLowerCase();
      return (
        orderUuid.includes(q) ||
        userName.includes(q) ||
        userMobile.includes(q) ||
        prodName.includes(q) ||
        reason.includes(q) ||
        id.includes(q)
      );
    });
  }, [returnList, searchText]);

  // Pagination logic
  const totalItems = filteredList.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedList = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredList.slice(startIndex, endIndex);
  }, [filteredList, currentPage, itemsPerPage]);

  const calculatePaidAmount = (item) => {
    const base = Number(item.amount || 0);
    const gst = Number(item.product?.gst || 0);
    if (gst > 0 && Math.abs(base - Number(item.product?.price || 0)) < 0.05) {
      return Number((base + (base * gst / 100)).toFixed(2));
    }
    return base;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const viewDetails = (item) => {
    navigate('./single', { state: { item: item.id } });
  };

  const getStatusBadge = (status) => {
    switch (String(status)) {
      case '1':
        return <Badge bg="warning" className="text-dark">Requested</Badge>;
      case '2':
        return <Badge bg="info" className="text-white">Approved</Badge>;
      case '3':
        return <Badge bg="primary">Picked Up</Badge>;
      case '4':
        return <Badge bg="success">Refunded</Badge>;
      case '5':
        return <Badge bg="danger">Denied</Badge>;
      default:
        return <Badge bg="secondary">Pending</Badge>;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchReturnList(selectedStatus);
  }, []);

  return (
    <div className="main-content">
      {loader ? (
        <Loader />
      ) : (
        <>
          <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} theme={'colored'} />
          <div className="section returnOrderList">
            <div className="row">
              <div className="col-md-12">
                <div className="section-title">
                  <h5><TbTruckReturn className="me-1" /> Returned Orders Management</h5>
                </div>

                {/* Filter Tabs */}
                <div className="count-section d-flex justify-content-left flex-wrap gap-2 mb-3 mt-2">
                  <button
                    className="product-page-block d-flex align-items-center me-2"
                    onClick={() => handleStatusFilter(undefined)}
                    style={selectedStatus === undefined ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === undefined ? styles.activeBtnTxt : null}>
                        <TbTruckReturn /> All ({counts.all})
                      </h5>
                    </div>
                  </button>

                  <button
                    className="product-page-block d-flex align-items-center me-2"
                    onClick={() => handleStatusFilter(1)}
                    style={selectedStatus === 1 ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === 1 ? styles.activeBtnTxt : null}>
                        <AiOutlineClockCircle /> Requested ({counts.requested})
                      </h5>
                    </div>
                  </button>

                  <button
                    className="product-page-block d-flex align-items-center me-2"
                    onClick={() => handleStatusFilter(2)}
                    style={selectedStatus === 2 ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === 2 ? styles.activeBtnTxt : null}>
                        <AiOutlineCheckCircle /> Approved ({counts.approved})
                      </h5>
                    </div>
                  </button>

                  <button
                    className="product-page-block d-flex align-items-center me-2"
                    onClick={() => handleStatusFilter(3)}
                    style={selectedStatus === 3 ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === 3 ? styles.activeBtnTxt : null}>
                        <BsBoxSeam /> Picked Up ({counts.picked_up})
                      </h5>
                    </div>
                  </button>

                  <button
                    className="product-page-block d-flex align-items-center me-2"
                    onClick={() => handleStatusFilter(4)}
                    style={selectedStatus === 4 ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === 4 ? styles.activeBtnTxt : null}>
                        <AiOutlineCheckCircle /> Refunded ({counts.refunded})
                      </h5>
                    </div>
                  </button>

                  <button
                    className="product-page-block d-flex align-items-center"
                    onClick={() => handleStatusFilter(5)}
                    style={selectedStatus === 5 ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === 5 ? styles.activeBtnTxt : null}>
                        <AiOutlineCloseCircle /> Denied ({counts.denied})
                      </h5>
                    </div>
                  </button>
                </div>

                {/* Table Card */}
                <div className="card p-2 mt-3">
                  <div className="card-title d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h5>You have {totalItems} Return Orders</h5>
                    <div style={{ maxWidth: '320px', width: '100%' }}>
                      <input
                        type="text"
                        placeholder="Search by Order ID, User, Product..."
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="form-control form-control-sm"
                        style={{ border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                      />
                    </div>
                  </div>

                  <div>
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th style={{ width: "50px" }}>SL</th>
                          <th>Order ID</th>
                          <th>Customer</th>
                          <th>Product</th>
                          <th>Amount</th>
                          <th>Return Reason</th>
                          <th className="text-center">Stage</th>
                          <th className="text-center">Requested On</th>
                          <th className="text-center" style={{ width: "120px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedList && paginatedList.length > 0 ? (
                          paginatedList.map((item, index) => {
                            const sl = ((currentPage - 1) * itemsPerPage) + index + 1;
                            return (
                              <tr key={item.id || index}>
                                <td>{sl}.</td>
                                <td>
                                  <strong>{item.order?.order_uuid || `#${item.order_id}`}</strong>
                                </td>
                                <td>
                                  <div><strong>{item.user?.name || "N/A"}</strong></div>
                                  <small className="text-muted">{item.user?.mobile}</small>
                                </td>
                                <td>
                                  <div>
                                    <div className="fw-semibold text-truncate" style={{ maxWidth: '240px' }} title={item.product?.name || "Product"}>
                                      {item.product?.name || "Product"}
                                    </div>
                                    {item.product_size?.name && (
                                      <small className="text-muted d-block">Size: {item.product_size.name}</small>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <strong>₹{calculatePaidAmount(item).toFixed(2)}</strong>
                                </td>
                                <td>
                                  <span className="text-truncate d-inline-block" style={{ maxWidth: '180px' }}>
                                    {item.return_reason || item.reason?.reason || item.remarks || "No reason"}
                                  </span>
                                </td>
                                <td className="text-center">
                                  {getStatusBadge(item.status)}
                                </td>
                                <td className="text-center">
                                  <small>{moment(item.created_at).format("DD MMM, YYYY")}</small>
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    onClick={() => viewDetails(item)}
                                    className="btn btn-xs btn-primary text-white"
                                  >
                                    View & Update
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-4 text-muted">
                              {searchText ? "No matching return orders found." : "No returned orders found."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    {totalItems > 0 && (
                      <div
                        className="flex items-center justify-between mt-4 px-2"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginTop: '1rem',
                          padding: '0 0.5rem',
                          flexWrap: 'wrap',
                          gap: '0.75rem'
                        }}
                      >
                        <div
                          className="flex items-center gap-2"
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                          <span className="text-sm text-gray-700" style={{ fontSize: '0.875rem', color: '#374151' }}>
                            Show
                          </span>
                          <select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                            style={{
                              padding: '0.25rem 0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              outline: 'none',
                              background: '#fff'
                            }}
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                          </select>
                          <span className="text-sm text-gray-700" style={{ fontSize: '0.875rem', color: '#374151' }}>
                            entries
                          </span>
                        </div>

                        <div
                          className="flex items-center gap-2"
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                          <button
                            type="button"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-black font-medium"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              background: '#fff',
                              color: '#000',
                              opacity: currentPage === 1 ? 0.5 : 1,
                              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <ChevronLeft className="h-4 w-4" style={{ height: '1rem', width: '1rem' }} /> Previous
                          </button>

                          <span
                            className="text-sm font-medium px-2 text-gray-700"
                            style={{ fontSize: '0.875rem', fontWeight: 500, padding: '0 0.5rem', color: '#374151' }}
                          >
                            Page {currentPage} of {totalPages}
                          </span>

                          <button
                            type="button"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-black font-medium"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              background: '#fff',
                              color: '#000',
                              opacity: currentPage === totalPages ? 0.5 : 1,
                              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Next <ChevronRight className="h-4 w-4" style={{ height: '1rem', width: '1rem' }} />
                          </button>
                        </div>

                        <div className="text-sm text-gray-700" style={{ fontSize: '0.875rem', color: '#374151' }}>
                          Showing {totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ReturnOrderList;

const styles = {
  activeBtn: { backgroundColor: '#ffc001ad' },
  activeBtnTxt: { color: '#000' },
};

